import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { cores } from '../../theme';
import {
    Typography, Container, Box, Button, Paper, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Alert, IconButton, Avatar, Fade, Dialog,
    DialogActions, DialogContent, DialogContentText, DialogTitle,
    Menu, MenuItem, ListItemIcon, ListItemText, useTheme, Divider
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack,
    Add as AddIcon,
    Assignment as AssignmentIcon,
    LibraryBooks as LibraryBooksIcon,
    Warning as WarningIcon,
    MoreVert as MoreVertIcon,
    CloudDownload as CloudDownloadIcon,
    CloudUpload as CloudUploadIcon,
    Close as CloseIcon
} from '@mui/icons-material';

const CasosGerenciamentoPagina = () => {
    const [casos, setCasos] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [dialogoConfirmacao, setDialogoConfirmacao] = useState({
        aberto: false,
        casoId: null,
        tituloCaso: ''
    });
    const [menuAnchorEl, setMenuAnchorEl] = useState({
        id: null,
        element: null
    });
    const [modalImportacaoAberto, setModalImportacaoAberto] = useState(false);
    const [arquivoSelecionado, setArquivoSelecionado] = useState(null);
    const [processandoImportacao, setProcessandoImportacao] = useState(false);
    const [resultadoImportacao, setResultadoImportacao] = useState(null);
    const [erroImportacao, setErroImportacao] = useState('');
    const navegar = useNavigate();
    const theme = useTheme();

    useEffect(() => {
        const buscarCasos = async () => {
            try {
                const resposta = await api.get('/api/casos-clinicos');
                setCasos(resposta.data);
            } catch (err) {
                setErro('Falha ao carregar os casos clínicos.');
                console.error(err);
            } finally {
                setCarregando(false);
            }
        };
        buscarCasos();
    }, []);

    const abrirMenuAcoes = (event, casoId) => {
        setMenuAnchorEl({
            id: casoId,
            element: event.currentTarget
        });
    };

    const fecharMenuAcoes = () => {
        setMenuAnchorEl({
            id: null,
            element: null
        });
    };

    const abrirDialogoConfirmacao = (casoId, tituloCaso) => {
        setDialogoConfirmacao({
            aberto: true,
            casoId,
            tituloCaso
        });
    };

    const fecharDialogoConfirmacao = () => {
        setDialogoConfirmacao({
            aberto: false,
            casoId: null,
            tituloCaso: ''
        });
    };

    const confirmarDelecao = async () => {
        try {
            await api.delete(`/api/casos-clinicos/${dialogoConfirmacao.casoId}`);
            setCasos(casosAtuais => casosAtuais.filter(caso => caso.id !== dialogoConfirmacao.casoId));
            fecharDialogoConfirmacao();
        } catch (err) {
            setErro(err.response?.data?.erro || "Erro ao deletar caso clínico.");
            fecharDialogoConfirmacao();
        }
    };

    const abrirModalImportacao = () => {
        setArquivoSelecionado(null);
        setResultadoImportacao(null);
        setErroImportacao('');
        setModalImportacaoAberto(true);
    };

    const lidarComSelecaoDeArquivo = (evento) => {
        setArquivoSelecionado(evento.target.files[0]);
        setErroImportacao('');
        setResultadoImportacao(null);
    };

    const lidarComDownloadTemplate = async () => {
        try {
            const resposta = await api.get('/api/casos-clinicos/template', {
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([resposta.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'template_casos.xlsx');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (err) {
            setErroImportacao("Erro ao baixar o template.");
        }
    };

    const lidarComUpload = async () => {
        if (!arquivoSelecionado) {
            setErroImportacao("Por favor, selecione um arquivo.");
            return;
        }
        setProcessandoImportacao(true);
        setResultadoImportacao(null);
        setErroImportacao('');

        const formData = new FormData();
        formData.append('planilha', arquivoSelecionado);

        try {
            const resposta = await api.post('/api/casos-clinicos/importar', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setResultadoImportacao(resposta.data);
            const buscarCasos = async () => {
                try {
                    const resposta = await api.get('/api/casos-clinicos');
                    setCasos(resposta.data);
                } catch (err) {
                    setErro('Falha ao carregar os casos clínicos.');
                    console.error(err);
                }
            };
            buscarCasos();
        } catch (err) {
            setErroImportacao(err.response?.data?.erro || "Ocorreu um erro na importação.");
        } finally {
            setProcessandoImportacao(false);
        }
    };

    if (carregando) {
        return (
            <Container maxWidth="lg" sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: cores.primaria.principal }} />
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
            <Fade in={true} timeout={500}>
                <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 4,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        background: cores.gradientes.primario,
                        color: 'white'
                    }}>
                        <IconButton
                            onClick={() => navegar('/gerenciar')}
                            sx={{
                                mr: 2,
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Gerenciamento de Casos Clínicos
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    opacity: 0.9,
                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                }}
                            >
                                Visualize, edite e crie novos casos clínicos
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: 2
                    }}>
                        <Box>
                            <Typography
                                variant="h5"
                                component="h2"
                                sx={{
                                    fontWeight: 700,
                                    color: cores.primaria.principal,
                                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                                }}
                            >
                                Lista de Casos Clínicos
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {casos.length} {casos.length === 1 ? 'caso encontrado' : 'casos encontrados'}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Button
                                variant="outlined"
                                onClick={abrirModalImportacao}
                                startIcon={<CloudUploadIcon />}
                                sx={{
                                    borderRadius: 2,
                                    borderColor: cores.cinza.medio,
                                    color: cores.secundaria.principal,
                                    '&:hover': {
                                        borderColor: cores.secundaria.principal,
                                        bgcolor: `rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.04)`
                                    }
                                }}
                            >
                                Importar Casos
                            </Button>
                            <Button
                                variant="contained"
                                component={RouterLink}
                                to="/casos/criar"
                                startIcon={<AddIcon />}
                                sx={{
                                    bgcolor: cores.primaria.principal,
                                    borderRadius: 2,
                                    boxShadow: cores.sombras.suave,
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1,
                                    '&:hover': {
                                        bgcolor: cores.primaria.escuro,
                                    }
                                }}
                            >
                                Criar Novo Caso
                            </Button>
                        </Box>
                    </Box>

                    {erro && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                backgroundColor: cores.erro.claro,
                                borderColor: cores.erro.medio,
                                color: cores.erro.escuro,
                                '& .MuiAlert-icon': {
                                    alignItems: 'center'
                                }
                            }}
                        >
                            {erro}
                        </Alert>
                    )}

                    <Fade in={true} timeout={500}>
                        <TableContainer
                            component={Paper}
                            sx={{
                                borderRadius: 2,
                                boxShadow: cores.sombras.suave,
                                overflow: 'hidden',
                                border: `1px solid ${cores.cinza.claro}`,
                            }}
                        >
                            <Table>
                                <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal }}>Título do Caso</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal }}>Data de Criação</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: cores.primaria.principal }}>Criado Por</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700, color: cores.primaria.principal }}>Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {casos.map((caso, index) => (
                                        <Fade in={true} timeout={300 + index * 100} key={caso.id}>
                                            <TableRow
                                                hover
                                                sx={{
                                                    '&:hover': {
                                                        bgcolor: '#f8fafc',
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            textDecoration: 'none',
                                                        }}
                                                    >
                                                        <Avatar
                                                            sx={{
                                                                mr: 2,
                                                                bgcolor: 'transparent',
                                                                color: cores.primaria.principal,
                                                                border: `2px solid ${cores.cinza.claro}`,
                                                                width: 36,
                                                                height: 36
                                                            }}
                                                        >
                                                            <AssignmentIcon />
                                                        </Avatar>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontSize: '1rem'
                                                            }}
                                                        >
                                                            {caso.titulo}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{
                                                            color: cores.cinza.escuro,
                                                            fontSize: '0.9rem'
                                                        }}
                                                    >
                                                        {new Date(caso.data_criacao).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{
                                                            color: cores.cinza.escuro,
                                                            fontSize: '0.9rem'
                                                        }}
                                                    >
                                                        {caso.criado_por}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <IconButton
                                                        onClick={(e) => abrirMenuAcoes(e, caso.id)}
                                                        size="small"
                                                        sx={{
                                                            border: `1px solid ${cores.cinza.claro}`,
                                                            '&:hover': {
                                                                bgcolor: '#f1f5f9',
                                                            }
                                                        }}
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        </Fade>
                                    ))}
                                    {casos.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body1" sx={{ color: cores.cinza.medio }}>
                                                    Nenhum caso clínico encontrado.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Fade>
                </Paper>
            </Fade>

            <Dialog
                open={dialogoConfirmacao.aberto}
                onClose={fecharDialogoConfirmacao}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: cores.sombras.forte,
                        maxWidth: 450
                    }
                }}
            >
                <DialogTitle sx={{
                    bgcolor: cores.erro.claro,
                    color: cores.erro.escuro,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700,
                    py: 2
                }}>
                    <WarningIcon color="error" fontSize="small" />
                    Confirmar Exclusão
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <DialogContentText sx={{ color: cores.cinza.escuro, mb: 2 }}>
                        Você está prestes a excluir o caso clínico <strong>{dialogoConfirmacao.tituloCaso}</strong>. Esta ação não poderá ser desfeita e todos os dados relacionados a este caso serão perdidos.
                    </DialogContentText>
                    <DialogContentText sx={{
                        color: cores.erro.escuro,
                        bgcolor: cores.erro.claro,
                        p: 2,
                        borderRadius: 2,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <WarningIcon fontSize="small" sx={{ color: cores.erro.medio }} />
                        Todas as avaliações associadas a este caso também serão excluídas.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                    <Button
                        onClick={fecharDialogoConfirmacao}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            color: cores.cinza.escuro,
                            borderColor: cores.cinza.claro,
                            '&:hover': {
                                backgroundColor: '#f1f5f9',
                                borderColor: cores.cinza.medio
                            },
                            px: 2
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={confirmarDelecao}
                        variant="contained"
                        color="error"
                        sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.2)`,
                            px: 3,
                            '&:hover': {
                                backgroundColor: cores.erro.escuro,
                                boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.3)`
                            }
                        }}
                    >
                        Excluir Caso
                    </Button>
                </DialogActions>
            </Dialog>

            <Menu
                anchorEl={menuAnchorEl.element}
                open={Boolean(menuAnchorEl.element)}
                onClose={fecharMenuAcoes}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    elevation: 2,
                    sx: {
                        borderRadius: 2,
                        minWidth: 180,
                        boxShadow: cores.sombras.medio,
                        '& .MuiList-root': {
                            py: 1
                        }
                    }
                }}
            >
                <MenuItem
                    component={RouterLink}
                    to={`/casos/detalhes/${menuAnchorEl.id}`}
                    onClick={fecharMenuAcoes}
                    sx={{ py: 1 }}
                >
                    <ListItemIcon>
                        <LibraryBooksIcon fontSize="small" sx={{ color: cores.secundaria.principal }} />
                    </ListItemIcon>
                    <ListItemText>Montar Rubrica</ListItemText>
                </MenuItem>
                <MenuItem
                    component={RouterLink}
                    to={`/casos/editar/${menuAnchorEl.id}`}
                    onClick={fecharMenuAcoes}
                    sx={{ py: 1 }}
                >
                    <ListItemIcon>
                        <EditIcon fontSize="small" sx={{ color: cores.primaria.principal }} />
                    </ListItemIcon>
                    <ListItemText>Editar</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        const caso = casos.find(c => c.id === menuAnchorEl.id);
                        abrirDialogoConfirmacao(menuAnchorEl.id, caso?.titulo || '');
                        fecharMenuAcoes();
                    }}
                    sx={{
                        py: 1,
                        color: cores.erro.principal
                    }}
                >
                    <ListItemIcon>
                        <DeleteIcon fontSize="small" sx={{ color: cores.erro.principal }} />
                    </ListItemIcon>
                    <ListItemText>Excluir</ListItemText>
                </MenuItem>
            </Menu>

            <Dialog
                open={modalImportacaoAberto}
                onClose={() => setModalImportacaoAberto(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: cores.sombras.forte
                    }
                }}
            >
                <DialogTitle sx={{
                    pb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: `rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.1)`, color: cores.secundaria.principal }}>
                            <CloudUploadIcon />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Importar Casos Clínicos
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setModalImportacaoAberto(false)} size="small" sx={{ color: cores.cinza.medio }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ my: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: cores.primaria.principal, mb: 1 }}>
                            Passo 1: Baixe o modelo
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={lidarComDownloadTemplate}
                            startIcon={<CloudDownloadIcon />}
                            sx={{
                                borderRadius: 2,
                                borderColor: cores.cinza.medio,
                                color: cores.secundaria.principal,
                                mb: 1,
                                '&:hover': {
                                    borderColor: cores.secundaria.principal,
                                    bgcolor: `rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.04)`
                                }
                            }}
                        >
                            Baixar planilha modelo
                        </Button>
                        <Typography variant="body2" color={cores.cinza.escuro}>
                            Preencha o arquivo com os dados dos casos clínicos (título, descrição) e salve em seu computador.
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ my: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: cores.primaria.principal, mb: 1 }}>
                            Passo 2: Envie o arquivo preenchido
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Button
                                variant="contained"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                                sx={{
                                    borderRadius: 2,
                                    bgcolor: cores.secundaria.principal,
                                    fontWeight: 600,
                                    boxShadow: cores.sombras.suave,
                                    '&:hover': {
                                        bgcolor: cores.secundaria.clara,
                                    }
                                }}
                            >
                                Selecionar Arquivo
                                <input type="file" hidden accept=".xlsx" onChange={lidarComSelecaoDeArquivo} />
                            </Button>
                            {arquivoSelecionado && (
                                <Typography variant="body2" sx={{ color: cores.cinza.escuro }}>
                                    {arquivoSelecionado.name}
                                </Typography>
                            )}
                        </Box>

                        {processandoImportacao && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                                <CircularProgress sx={{ color: cores.primaria.principal }} />
                            </Box>
                        )}

                        {resultadoImportacao && (
                            <Alert
                                severity="success"
                                sx={{
                                    mt: 2,
                                    borderRadius: 2,
                                    backgroundColor: cores.sucesso.claro || '#dcfce7',
                                    color: cores.sucesso.escuro || '#166534',
                                    '& .MuiAlert-icon': {
                                        alignItems: 'center'
                                    }
                                }}
                            >
                                Importação Concluída! {resultadoImportacao.totalImportado} casos processados.
                            </Alert>
                        )}

                        {erroImportacao && (
                            <Alert
                                severity="error"
                                sx={{
                                    mt: 2,
                                    borderRadius: 2,
                                    backgroundColor: cores.erro.claro,
                                    borderColor: cores.erro.medio,
                                    color: cores.erro.escuro,
                                    '& .MuiAlert-icon': {
                                        alignItems: 'center'
                                    }
                                }}
                            >
                                {erroImportacao}
                            </Alert>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setModalImportacaoAberto(false)}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            borderColor: cores.cinza.claro,
                            color: cores.cinza.escuro,
                            '&:hover': {
                                borderColor: cores.cinza.medio,
                                bgcolor: 'rgba(203, 213, 225, 0.1)'
                            }
                        }}
                    >
                        Fechar
                    </Button>
                    <Button
                        onClick={lidarComUpload}
                        disabled={!arquivoSelecionado || processandoImportacao}
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        sx={{
                            borderRadius: 2,
                            bgcolor: cores.primaria.principal,
                            fontWeight: 600,
                            '&:hover': {
                                bgcolor: cores.primaria.escuro,
                            },
                            '&.Mui-disabled': {
                                bgcolor: cores.cinza.claro,
                                color: cores.cinza.medio
                            }
                        }}
                    >
                        {processandoImportacao ? 'Processando...' : 'Enviar e Processar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default CasosGerenciamentoPagina;